import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription} from '../../components/ui/dialog';
import {
  ArrowLeft, Plus, Copy, Edit3, Trash2, Sparkles, Loader2, Save, Send, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDateMMDDYYYY } from '../../utils/timeFormat';

const EMPTY = {
  name: '',
  category: 'general',
  subject: '',
  body_html: '',
  body_text: '',
  sms_body: '',
};

const CATEGORIES = ['general', 'announcement', 'reminder', 'activation', 'recruitment', 'invite'];

const AdminBroadcastTemplates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { ...template } or null
  const [saving, setSaving] = useState(false);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiForm, setAiForm] = useState({ purpose: '', audience: 'talent', tone: 'friendly' });
  const [aiLoading, setAiLoading] = useState(false);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/broadcast-templates');
      setTemplates(res.data.templates || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetch(); }, [fetch]);

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) {
      toast.error('Template name is required');
      return;
    }
    setSaving(true);
    try {
      if (editing.id) {
        await api.patch(`/admin/broadcast-templates/${editing.id}`, editing);
        toast.success('Template updated');
      } else {
        await api.post('/admin/broadcast-templates', editing);
        toast.success('Template saved');
      }
      setEditing(null);
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const duplicate = async (id) => {
    try {
      await api.post(`/admin/broadcast-templates/${id}/duplicate`);
      toast.success('Template duplicated');
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to duplicate');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Archive this template?')) return;
    try {
      await api.delete(`/admin/broadcast-templates/${id}`);
      toast.success('Template archived');
      fetch();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to archive');
    }
  };

  const generateAI = async () => {
    if (!aiForm.purpose.trim() || aiForm.purpose.trim().length < 5) {
      toast.error('Describe the purpose in more detail');
      return;
    }
    setAiLoading(true);
    try {
      const res = await api.post('/admin/broadcast-templates/generate', aiForm);
      setAiOpen(false);
      setEditing({
        name: res.data.name,
        category: res.data.category || 'general',
        subject: res.data.subject || '',
        body_html: res.data.body_html || '',
        body_text: res.data.body_text || '',
        sms_body: res.data.sms_body || '',
        ai_generated: true,
      });
      toast.success('AI draft ready — review and Save');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate');
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="broadcast-templates-page">
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/broadcast')} data-testid="back-to-broadcast-btn">
              <ArrowLeft className="w-4 h-4 mr-1" /> Broadcast
            </Button>
            <h1 className="text-lg font-semibold">Broadcast Templates</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => { setAiForm({ purpose: '', audience: 'talent', tone: 'friendly' }); setAiOpen(true); }}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
              data-testid="ai-generate-btn"
            >
              <Sparkles className="w-4 h-4 mr-2" /> AI Draft
            </Button>
            <Button
              onClick={() => setEditing({ ...EMPTY })}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="new-template-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> New Template
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-cyan-600" />
          </div>
        ) : templates.length === 0 ? (
          <Card><CardContent className="py-12 text-center text-gray-500">
            <Send className="w-10 h-10 mx-auto mb-2 text-gray-300" />
            <p>No templates yet. Create one or generate with AI.</p>
          </CardContent></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="templates-grid">
            {templates.map((t) => (
              <Card key={t.id} className="hover:shadow transition" data-testid={`template-card-${t.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm">{t.name}</CardTitle>
                    {t.ai_generated && (
                      <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                        <Sparkles className="w-2.5 h-2.5 mr-0.5" /> AI
                      </Badge>
                    )}
                  </div>
                  <Badge variant="outline" className="text-[10px] w-fit">{t.category}</Badge>
                </CardHeader>
                <CardContent className="space-y-2">
                  {t.subject && (
                    <div className="text-xs">
                      <span className="text-gray-500">Subject:</span> <span className="text-gray-800 font-medium">{t.subject}</span>
                    </div>
                  )}
                  {t.body_text && (
                    <p className="text-xs text-gray-600 line-clamp-3 whitespace-pre-wrap">{t.body_text}</p>
                  )}
                  {t.sms_body && (
                    <div className="text-xs bg-amber-50 border border-amber-100 rounded p-2 text-amber-900 line-clamp-2">
                      SMS: {t.sms_body}
                    </div>
                  )}
                  <div className="flex items-center gap-1 pt-1">
                    <Button size="sm" variant="outline" onClick={() => setEditing({ ...t })} data-testid={`edit-tpl-${t.id}`}>
                      <Edit3 className="w-3 h-3 mr-1" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => duplicate(t.id)} data-testid={`dup-tpl-${t.id}`}>
                      <Copy className="w-3 h-3 mr-1" /> Duplicate
                    </Button>
                    <Button size="sm" variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => remove(t.id)} data-testid={`del-tpl-${t.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <p className="text-[10px] text-gray-400 text-right">Updated {formatDateMMDDYYYY(t.updated_at)}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Edit / Create dialog */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? 'Edit Template' : 'New Template'}</DialogTitle>
          <DialogDescription className="sr-only">Dialog actions</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Name</Label>
                  <Input
                    value={editing.name || ''}
                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                    data-testid="tpl-name-input"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Select
                    value={editing.category || 'general'}
                    onValueChange={(v) => setEditing({ ...editing, category: v })}
                  >
                    <SelectTrigger data-testid="tpl-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Subject (email)</Label>
                <Input
                  value={editing.subject || ''}
                  onChange={(e) => setEditing({ ...editing, subject: e.target.value })}
                  placeholder="Hi {{first_name}}, …"
                  data-testid="tpl-subject-input"
                />
              </div>

              <div>
                <Label>Email body (HTML)</Label>
                <Textarea
                  rows={6}
                  value={editing.body_html || ''}
                  onChange={(e) => setEditing({ ...editing, body_html: e.target.value })}
                  placeholder="<p>Hi {{first_name}}, …</p>"
                  data-testid="tpl-body-html-input"
                />
              </div>

              <div>
                <Label>Email body (plain text fallback)</Label>
                <Textarea
                  rows={4}
                  value={editing.body_text || ''}
                  onChange={(e) => setEditing({ ...editing, body_text: e.target.value })}
                  data-testid="tpl-body-text-input"
                />
              </div>

              <div>
                <Label>SMS body (max 1600)</Label>
                <Textarea
                  rows={2}
                  value={editing.sms_body || ''}
                  onChange={(e) => setEditing({ ...editing, sms_body: e.target.value })}
                  placeholder="Short version for SMS. Include {{app_url}} if needed."
                  data-testid="tpl-sms-input"
                />
                <p className="text-[11px] text-gray-500 mt-1">{(editing.sms_body || '').length}/1600 chars</p>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded p-2 text-[11px] text-gray-600">
                <span className="font-medium">Variables:</span> {'{{first_name}}'}, {'{{event_name}}'}, {'{{shift_date}}'}, {'{{venue_name}}'}, {'{{app_url}}'}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button
              onClick={save}
              disabled={saving || !editing?.name?.trim()}
              className="bg-cyan-500 hover:bg-cyan-600 text-white"
              data-testid="save-template-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              {editing?.id ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Generate dialog */}
      <Dialog open={aiOpen} onOpenChange={setAiOpen}>
        <DialogContent className="bg-white max-w-md" data-testid="ai-generate-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-purple-600" /> AI Draft Generator
            </DialogTitle>
          <DialogDescription className="sr-only">AI Draft Generator</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Purpose / prompt</Label>
              <Textarea
                rows={4}
                value={aiForm.purpose}
                onChange={(e) => setAiForm({ ...aiForm, purpose: e.target.value })}
                placeholder="e.g. Remind talent that tomorrow's shift starts at 9am; ask them to arrive 15 min early and bring black pants."
                data-testid="ai-purpose-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Audience</Label>
                <Select value={aiForm.audience} onValueChange={(v) => setAiForm({ ...aiForm, audience: v })}>
                  <SelectTrigger data-testid="ai-audience-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="talent">Talent</SelectItem>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tone</Label>
                <Select value={aiForm.tone} onValueChange={(v) => setAiForm({ ...aiForm, tone: v })}>
                  <SelectTrigger data-testid="ai-tone-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="friendly">Friendly</SelectItem>
                    <SelectItem value="professional">Professional</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                    <SelectItem value="casual">Casual</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAiOpen(false)} disabled={aiLoading}>Cancel</Button>
            <Button
              onClick={generateAI}
              disabled={aiLoading || !aiForm.purpose.trim()}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="ai-generate-submit-btn"
            >
              {aiLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              Generate Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminBroadcastTemplates;
